var _effectorFileName = "/src/shared/lib/locale-storage/index.ts";
import Cookies from 'js-cookie';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@/shared/config/i18n';
const LOCALE_COOKIE_NAME = 'NEXT_LOCALE';
const LOCALE_STORAGE_KEY = 'preferred-locale';

/**
 * Save locale preference to both cookie (for SSR) and localStorage (for client fallback)
 */
export const saveLocalePreference = (locale: Locale): void => {
  // Cookie for SSR - Next.js reads this automatically
  Cookies.set(LOCALE_COOKIE_NAME, locale, {
    expires: 365,
    // 1 year
    path: '/',
    sameSite: 'lax'
  });

  // localStorage as fallback
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // localStorage might be unavailable (private browsing, etc.)
    }
  }
};

/**
 * Get saved locale preference from cookie or localStorage
 */
export const getSavedLocale = (): Locale | null => {
  // Try cookie first
  const cookieLocale = Cookies.get(LOCALE_COOKIE_NAME);
  if (cookieLocale && LOCALES.includes(cookieLocale as Locale)) {
    return cookieLocale as Locale;
  }

  // Fallback to localStorage
  if (typeof window !== 'undefined') {
    try {
      const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (storedLocale && LOCALES.includes(storedLocale as Locale)) {
        return storedLocale as Locale;
      }
    } catch {
      // localStorage might be unavailable
    }
  }
  return null;
};

/**
 * Get locale from cookie on server-side (for getServerSideProps)
 */
export const getLocaleFromCookies = (cookieHeader?: string): Locale => {
  if (!cookieHeader) return DEFAULT_LOCALE;
  const cookies = cookieHeader.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    if (key && value) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, string>);
  const locale = cookies[LOCALE_COOKIE_NAME];
  if (locale && LOCALES.includes(locale as Locale)) {
    return locale as Locale;
  }
  return DEFAULT_LOCALE;
};